/**
 * Updates tab - Real-time MCP activity stream
 * Story 234: Real-time MCP Updates Stream
 *
 * Displays real-time events from agent activity, filtered by story.
 */

import { useState, useMemo, useCallback } from 'react';
import type { McpStreamEvent, ConnectionStatus } from '../../hooks/useUpdatesStream';
import { useStoryEvents } from '../../hooks/useStoryEvents';
import './UpdatesTab.css';

/** Operation badge colors */
const OPERATION_BADGES: Record<string, { label: string; className: string }> = {
  create: { label: 'Created', className: 'updates-tab__badge--create' },
  update: { label: 'Updated', className: 'updates-tab__badge--update' },
  delete: { label: 'Deleted', className: 'updates-tab__badge--delete' },
  start: { label: 'Started', className: 'updates-tab__badge--start' },
  complete: { label: 'Completed', className: 'updates-tab__badge--complete' },
};

/** Format timestamp to relative time */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

interface UpdateEntryProps {
  event: McpStreamEvent;
  onNavigate: (tab: 'tasks' | 'prd' | 'design', entityId?: string) => void;
}

function UpdateEntry({ event, onNavigate }: UpdateEntryProps) {
  const [expanded, setExpanded] = useState(false);
  const badge = OPERATION_BADGES[event.operation] || OPERATION_BADGES.update;

  const handleClick = () => {
    const { tab, entityId } = event.navigationTarget;
    if (tab !== 'overview') {
      onNavigate(tab, entityId);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <div className="updates-tab__entry" onClick={handleClick}>
      <div className="updates-tab__entry-main">
        <span className="updates-tab__timestamp">{formatRelativeTime(event.timestamp)}</span>
        <span className={`updates-tab__badge ${badge.className}`}>{badge.label}</span>
        <span className="updates-tab__summary">{event.summary}</span>
        <span className="updates-tab__actor">
          {/* Story 556: 'api' is the EngOps script, run by an operator from a terminal. */}
          {event.actorSource === 'mcp' ? 'Agent' : event.actorSource === 'api' ? 'Operator' : 'Web'}
        </span>
        {event.details && (
          <button
            className="updates-tab__expand-btn"
            onClick={handleExpandClick}
            type="button"
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? '\u25B2' : '\u25BC'}
          </button>
        )}
      </div>
      {expanded && event.details && (
        <div className="updates-tab__details">
          <pre>{JSON.stringify(event.details, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

interface UpdatesTabProps {
  projectSlug: string;
  storyId: string;
  onNavigate?: (tab: 'tasks' | 'prd' | 'design', sectionKey?: string) => void;
  /** Real-time events from Workspace (Story 253) */
  realtimeEvents?: McpStreamEvent[];
  /** WebSocket connection status */
  connectionStatus?: ConnectionStatus;
  /** Currently subscribed project ID */
  subscribedProjectId?: string | null;
  /** Connection error message */
  connectionError?: string | null;
  /** Callback to reconnect WebSocket */
  onReconnect?: () => void;
}

export function UpdatesTab({
  projectSlug,
  storyId,
  onNavigate,
  realtimeEvents = [],
  connectionStatus = 'disconnected',
  subscribedProjectId = null,
  connectionError = null,
  onReconnect,
}: UpdatesTabProps) {
  const [dateFilter, setDateFilter] = useState<'1d' | '7d' | '30d' | 'all'>('7d');

  // Fetch historical events from API
  const { data: historicalEvents, isLoading: historyLoading } = useStoryEvents(projectSlug, storyId);

  // Story 253: Real-time events now come from Workspace level via props
  // No longer managing our own WebSocket subscription here

  // Merge historical and real-time events, deduplicate by ID
  const mergedEvents = useMemo(() => {
    const eventMap = new Map<string, McpStreamEvent>();

    // Add historical events first (will be overwritten by real-time if same ID)
    if (historicalEvents) {
      for (const event of historicalEvents) {
        eventMap.set(event.id, event);
      }
    }

    // Add real-time events (newer, takes precedence)
    for (const event of realtimeEvents) {
      eventMap.set(event.id, event);
    }

    // Sort by timestamp descending (most recent first)
    return Array.from(eventMap.values()).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [historicalEvents, realtimeEvents]);

  // Filter events by story and date
  const filteredEvents = useMemo(() => {
    let filtered = mergedEvents.filter((e) => e.storyId === storyId);

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = Date.now();
      const daysMs = {
        '1d': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000,
      };
      const cutoff = now - daysMs[dateFilter];
      filtered = filtered.filter((e) => new Date(e.timestamp).getTime() > cutoff);
    }

    return filtered;
  }, [mergedEvents, storyId, dateFilter]);

  const handleNavigate = useCallback(
    (tab: 'tasks' | 'prd' | 'design', entityId?: string) => {
      if (onNavigate) {
        // Convert entityId to section key format if needed
        const sectionKey = entityId?.includes('.') ? `task-${entityId.split('.')[1]}` : entityId;
        onNavigate(tab, sectionKey);
      }
    },
    [onNavigate]
  );

  const connectionDotClass = connectionStatus === 'connected'
    ? 'updates-tab__status-dot--connected'
    : connectionStatus === 'reconnecting'
    ? 'updates-tab__status-dot--reconnecting'
    : 'updates-tab__status-dot--disconnected';

  return (
    <div className="updates-tab">
      <div className="updates-tab__header">
        <div className="updates-tab__status">
          <span className={`updates-tab__status-dot ${connectionDotClass}`} />
          <span className="updates-tab__status-text">
            {connectionStatus === 'connected'
              ? subscribedProjectId
                ? 'Subscribed'
                : 'Connected'
              : connectionStatus}
          </span>
          {connectionError && (
            <span className="updates-tab__error" title={connectionError}>
              Error: {connectionError}
            </span>
          )}
        </div>
        <div className="updates-tab__filters">
          <select
            className="updates-tab__date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
          >
            <option value="1d">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
        {connectionStatus === 'disconnected' && onReconnect && (
          <button
            className="updates-tab__action-btn"
            onClick={onReconnect}
            type="button"
          >
            Reconnect
          </button>
        )}
      </div>
      <div className="updates-tab__list">
        {historyLoading ? (
          <div className="updates-tab__loading">
            <p>Loading history...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="updates-tab__empty">
            <p>No updates for this story yet.</p>
            <p className="updates-tab__empty-hint">
              Updates will appear here when agents modify this story.
            </p>
          </div>
        ) : (
          filteredEvents.map((event) => (
            <UpdateEntry
              key={event.id}
              event={event}
              onNavigate={handleNavigate}
            />
          ))
        )}
      </div>
    </div>
  );
}
